import PropTypes from 'prop-types';
import { createContext, useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { LoyaltyService } from 'utils/api-service';
import { subscribe, unsubscribe } from 'utils/global-events';
import WebClient from 'utils/webclient';
import * as Redux from 'utils/redux-store';

const MembershipContext = createContext();

const getCurrentMember = () => Redux.store.getState()?.member ?? null;

// logout basladiysa veya oturum degistiyse onceki oturumun yanitlari islenmez
const isSessionStale = member =>
  member == null || Redux.isLogoutInProgress() || getCurrentMember() !== member;

export const MembershipContextProvider = ({ children }) => {
  const [membership, setMembership] = useState(null);
  const [patientMembership, setPatientMembership] = useState(null);
  const [patientCard, setPatientCard] = useState(null);
  const [membershipCards, setMembershipCards] = useState([]);
  const [upgradeableCardTypes, setUpgradeableCardTypes] = useState([]);
  const [isLoyaltyActionsShown, setIsLoyaltyActionsShown] = useState(null);
  const [healthpassLabel, setHealthpassLabel] = useState(null);
  const c = new WebClient();

  const member = useSelector(state => state.member);
  const selectedHospital = useSelector(state => state.selectedHospital);

  const LS = useMemo(() => new LoyaltyService(), []);

  const getCardTypeList = useCallback(async () => {
    const { data, isSuccess } = await LS.getCardTypeList();
    if (isSuccess) {
      let initialCard = data.find(item => item.ishaveThisCard)
        ? data.find(item => item.ishaveThisCard)
        : data.find(item => item.isDefault);
      setPatientCard(initialCard);
      const _upgradeableCardTypes = data.filter(item => item.messageViewType != 0);
      setMembershipCards(data);
      setUpgradeableCardTypes(_upgradeableCardTypes);
    }
  }, [LS]);

  const patientCardInformation = useCallback(async () => {
    console.log('patientCardInformation');
    const requestMember = getCurrentMember();
    if (isSessionStale(requestMember)) return;

    let response;
    try {
      response = await LS.patientCardInformation();
    } catch {
      return;
    }
    if (isSessionStale(requestMember)) return;

    const { data, isSuccess } = response;
    if (isSuccess) {
      if (data?.loyalty) {
        setMembership(data?.loyalty);
        if (data?.loyalty?.isMember || data?.loyalty?.usabilityStatus) {
          getCardTypeList();
        }
      }

      if (data?.patient) {
        console.log(data?.patient);
        const colors = data?.patient?.cardColor.split(';').map(color => `#${color}`);
        const colorStops = colors.map((color, index) => {
          let position;
          if (index === 0) {
            position = 10;
          } else if (index === colors.length - 1) {
            position = 80;
          } else {
            position = 30;
          }
          return `${color} ${position}%`;
        });
        const bgGradientRight = `linear-gradient(to right, ${colorStops.join(', ')})`;
        const bgGradientBottom = `linear-gradient(to bottom, ${colorStops.join(', ')})`;
        const bgGradientWithDegree = `linear-gradient(225deg, ${colors.join(', ')})`;
        const color = colors[2];

        const _patientMembership = {
          ...data?.patient,
          styles: {
            bgGradientRight,
            bgGradientBottom,
            bgGradientWithDegree,
            color,
          },
        };

        setPatientMembership(_patientMembership);
      }
    }
  }, [LS, getCardTypeList]);

  // parameters apisinden baska kullanim olursa contexte cevirip, membership-context icine alacak sekilde kullanilabilir
  const getParameters = useCallback(async () => {
    const requestMember = getCurrentMember();
    const { data, isSuccess } = await c.post('573D6FBC30D637/parameter-list.json', {});
    if (isSuccess && !isSessionStale(requestMember)) {
      const { oldLoyaltyModulesAvailability, healthpassLabel } = data;
      setIsLoyaltyActionsShown(oldLoyaltyModulesAvailability); // oldLoyaltyModulesAvailability false ise gizlenecek, true ise gösterilecek
      setHealthpassLabel(healthpassLabel);

      patientCardInformation();
    }
  }, [c]);

  useEffect(() => {
    if (member != null && selectedHospital != null) {
      getParameters();
    }
    subscribe('onPatientCardInformmationRefresh', () => {
      patientCardInformation();
    });

    return () => {
      unsubscribe('onPatientCardInformmationRefresh');
    };
  }, [member, patientCardInformation, selectedHospital]);

  const contextValue = useMemo(
    () => ({
      membership,
      patientMembership,
      patientCard,
      membershipCards,
      upgradeableCardTypes,
      isLoyaltyActionsShown,
      healthpassLabel,
    }),
    [
      membership,
      patientMembership,
      membershipCards,
      upgradeableCardTypes,
      patientCard,
      isLoyaltyActionsShown,
      healthpassLabel,
    ]
  );

  return <MembershipContext.Provider value={contextValue}>{children}</MembershipContext.Provider>;
};

MembershipContextProvider.propTypes = {
  children: PropTypes.node,
};

export const useMembershipContext = () => {
  return useContext(MembershipContext);
};
